/**
 * Dashboard Preferences Manager
 * Manages user preferences for dashboard block visibility using localStorage
 * TODO: Refactor to use database storage in the future
 */

export interface DashboardBlock {
  id: string;
  name: string;
  visible: boolean;
  order: number;
}

export interface GridLayoutItem {
  i: string;  // block id
  x: number;  // column position (0-2 for 3 columns)
  y: number;  // row position
  w: number;  // width in columns (1-3)
  h: number;  // height in grid units
  minW?: number;
  minH?: number;
  maxW?: number;
}

export type DashboardLayout = 'grid' | 'masonry';

export interface DashboardPreferences {
  blocks: DashboardBlock[];
  layout: DashboardLayout;
}

const STORAGE_KEY = 'yallyhub_dashboard_preferences';
const LAYOUT_KEY = 'yallyhub_dashboard_layout';
const GRID_LAYOUT_KEY = 'yallyhub_dashboard_grid_layout';
const MIGRATION_KEY = 'yallyhub_dashboard_migrations';

const DEFAULT_BLOCKS: DashboardBlock[] = [
  { id: 'appointments-today', name: 'Vandaag in de agenda', visible: true, order: 0 },
  { id: 'metrics-overview', name: 'Belangrijkste cijfers', visible: true, order: 1 },
  { id: 'company-portfolio', name: 'Statistieken per vennootschap', visible: true, order: 2 },
  { id: 'contractual-rent', name: 'Contractuele huur', visible: true, order: 2.5 },
  { id: 'helpdesk-metrics', name: 'Helpdesk Metrics', visible: true, order: 3 },
  { id: 'vacancy-metrics', name: 'Vacancy Metrics', visible: true, order: 4 },
  // { id: 'summary-metrics', name: 'Yally Metrics', visible: true, order: 5 }, // Replaced by metrics-overview
  { id: 'helpdesk-tickets', name: 'Nieuwe Helpdesktickets', visible: true, order: 6 },
  { id: 'candidates', name: 'Kandidaten', visible: true, order: 7 },
  { id: 'tasks', name: 'Taken van vandaag', visible: true, order: 8 },
  { id: 'slack-feed', name: 'Aankondigingen', visible: true, order: 9 },
  { id: 'documents', name: 'Documenten', visible: true, order: 10 },
  { id: 'epc-metrics', name: 'EPC Prestaties', visible: true, order: 11 },
  { id: 'real-estate-metrics', name: 'Vastgoed Portfolio', visible: true, order: 12 },
  { id: 'renovation-photos', name: 'Recente renovatie foto\'s', visible: true, order: 13 },
];

const DEFAULT_LAYOUT: DashboardLayout = 'grid';

export class DashboardPreferencesManager {
  /**
   * Get all dashboard block preferences
   */
  static getPreferences(): DashboardBlock[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure new blocks are included
        const merged = this.mergeWithDefaults(parsed);
        return this.applyMigrations(merged);
      }
    } catch (error) {
      console.error('Error reading dashboard preferences:', error);
    }
    return DEFAULT_BLOCKS;
  }

  /**
   * One-time migrations for moving blocks that were introduced at a
   * suboptimal default order. Each migration runs at most once per browser.
   */
  private static applyMigrations(blocks: DashboardBlock[]): DashboardBlock[] {
    let applied: string[] = [];
    try {
      const raw = localStorage.getItem(MIGRATION_KEY);
      applied = raw ? JSON.parse(raw) : [];
    } catch {
      applied = [];
    }

    let next = blocks;
    let changed = false;

    // v1: promote company-portfolio to the top (right after metrics-overview)
    // for existing users who only ever saw it at the bottom.
    if (!applied.includes('company-portfolio-to-top-v1')) {
      const target = next.find((b) => b.id === 'company-portfolio');
      if (target) {
        const reordered = next
          .filter((b) => b.id !== 'company-portfolio')
          .map((b) => ({ ...b }));
        // Insert right after metrics-overview (or at index 0 if absent).
        const insertIndex =
          reordered.findIndex((b) => b.id === 'metrics-overview') + 1;
        reordered.splice(insertIndex, 0, { ...target });
        next = reordered.map((b, idx) => ({ ...b, order: idx }));
        changed = true;
      }
      applied.push('company-portfolio-to-top-v1');
    }

    // v2: place "Vandaag in de agenda" at index 0 for existing users.
    // It only entered DEFAULT_BLOCKS recently, so for accounts that already
    // had stored prefs the merge-with-defaults appends it to the bottom.
    if (!applied.includes('appointments-today-to-top-v1')) {
      const target = next.find((b) => b.id === 'appointments-today');
      if (target) {
        const reordered = next
          .filter((b) => b.id !== 'appointments-today')
          .map((b) => ({ ...b }));
        reordered.unshift({ ...target });
        next = reordered.map((b, idx) => ({ ...b, order: idx }));
        changed = true;
      }
      applied.push('appointments-today-to-top-v1');
    }

    // v3: "Contractuele huur" is new — place it right after company-portfolio
    // instead of wherever merge-with-defaults left it.
    if (!applied.includes('contractual-rent-after-portfolio-v1')) {
      const target = next.find((b) => b.id === 'contractual-rent');
      if (target) {
        const reordered = next
          .filter((b) => b.id !== 'contractual-rent')
          .map((b) => ({ ...b }));
        const insertIndex =
          reordered.findIndex((b) => b.id === 'company-portfolio') + 1;
        reordered.splice(insertIndex, 0, { ...target, visible: true });
        next = reordered.map((b, idx) => ({ ...b, order: idx }));
        changed = true;
      }
      applied.push('contractual-rent-after-portfolio-v1');
    }

    if (changed) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        localStorage.setItem(MIGRATION_KEY, JSON.stringify(applied));
      } catch (error) {
        console.error('Error persisting dashboard migration:', error);
      }
    } else {
      try {
        localStorage.setItem(MIGRATION_KEY, JSON.stringify(applied));
      } catch {
        // non-fatal
      }
    }

    return next;
  }

  /**
   * Save dashboard block preferences
   */
  static savePreferences(blocks: DashboardBlock[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blocks));
    } catch (error) {
      console.error('Error saving dashboard preferences:', error);
    }
  }

  /**
   * Toggle visibility of a specific block
   */
  static toggleBlockVisibility(blockId: string): void {
    const blocks = this.getPreferences();
    const updatedBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, visible: !block.visible } : block
    );
    this.savePreferences(updatedBlocks);
  }

  /**
   * Reset preferences to default
   */
  static resetToDefaults(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(GRID_LAYOUT_KEY);
      localStorage.removeItem(LAYOUT_KEY);
    } catch (error) {
      console.error('Error resetting dashboard preferences:', error);
    }
  }

  /**
   * Get only visible blocks, sorted by order
   */
  static getVisibleBlocks(): DashboardBlock[] {
    return this.getPreferences()
      .filter(block => block.visible)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Merge stored preferences with default blocks to ensure new blocks appear
   */
  private static mergeWithDefaults(stored: DashboardBlock[]): DashboardBlock[] {
    const storedMap = new Map(stored.map(block => [block.id, block]));
    
    return DEFAULT_BLOCKS.map(defaultBlock => {
      const storedBlock = storedMap.get(defaultBlock.id);
      return storedBlock || defaultBlock;
    });
  }

  /**
   * Update block order (for future drag-and-drop functionality)
   */
  static updateBlockOrder(blockId: string, newOrder: number): void {
    const blocks = this.getPreferences();
    const updatedBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, order: newOrder } : block
    );
    this.savePreferences(updatedBlocks);
  }

  /**
   * Get dashboard layout preference
   */
  static getLayout(): DashboardLayout {
    try {
      const stored = localStorage.getItem(LAYOUT_KEY);
      if (stored && (stored === 'grid' || stored === 'masonry')) {
        return stored as DashboardLayout;
      }
    } catch (error) {
      console.error('Error reading layout preference:', error);
    }
    return DEFAULT_LAYOUT;
  }

  /**
   * Save dashboard layout preference
   */
  static saveLayout(layout: DashboardLayout): void {
    try {
      localStorage.setItem(LAYOUT_KEY, layout);
    } catch (error) {
      console.error('Error saving layout preference:', error);
    }
  }

  /**
   * Get grid layout positions
   */
  static getGridLayout(): GridLayoutItem[] {
    try {
      const stored = localStorage.getItem(GRID_LAYOUT_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading grid layout:', error);
    }
    return this.getDefaultGridLayout();
  }

  /**
   * Save grid layout positions
   */
  static saveGridLayout(layout: GridLayoutItem[]): void {
    try {
      localStorage.setItem(GRID_LAYOUT_KEY, JSON.stringify(layout));
    } catch (error) {
      console.error('Error saving grid layout:', error);
    }
  }

  /**
   * Get default grid layout for all blocks (3-column grid)
   */
  private static getDefaultGridLayout(): GridLayoutItem[] {
    return DEFAULT_BLOCKS.map((block, index) => {
      // Metrics overview spans full width
      if (block.id === 'metrics-overview') {
        return {
          i: block.id,
          x: 0,
          y: 0,
          w: 3,  // Full width
          h: 3,  // Slightly shorter height
          minW: 3,
          minH: 2,
          maxW: 3,
        };
      }
      
      // Calculate position for other blocks (accounting for full-width block)
      const adjustedIndex = index - 1; // Subtract 1 because metrics-overview is first
      return {
        i: block.id,
        x: adjustedIndex % 3,  // 0, 1, 2, 0, 1, 2, ...
        y: Math.floor(adjustedIndex / 3) * 4 + 3,  // Start after the full-width block
        w: 1,  // 1 column wide
        h: 4,  // 4 units high
        minW: 1,
        minH: 2,
        maxW: 3,
      };
    });
  }
}

